// CountryFilter.tsx
import {Autocomplete, Chip, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useMemo, useEffect, useState} from "react";

import countriesFr from "../locales/fr/countries.json";
import countriesDe from "../locales/de/countries.json";
import countriesIt from "../locales/it/countries.json";

import mainsFr from "../locales/fr/mains.json";
import mainsDe from "../locales/de/mains.json";
import mainsIt from "../locales/it/mains.json";

interface Props {
    countrySearch: string[];
    setCountrySearch: (countries: string[]) => void;
}

type CountriesType = typeof countriesFr;
type MainsType = typeof mainsFr;

type Option = { code: string; name: string };

export default function CountryFilter({countrySearch, setCountrySearch}: Props) {
    const {t, i18n} = useTranslation();
    const currentLocale = i18n.language;

    const [countries, setCountries] = useState<CountriesType | null>(null);
    const [mains, setMains] = useState<MainsType | null>(null);

    useEffect(() => {
        switch (currentLocale) {
            case "de":
                setCountries(countriesDe);
                setMains(mainsDe);
                break;
            case "it":
                setCountries(countriesIt);
                setMains(mainsIt);
                break;
            case "fr":
            default:
                setCountries(countriesFr);
                setMains(mainsFr);
        }
    }, [currentLocale]);

    const options: Option[] = useMemo(() => {
        if (!countries || !mains) return [];
        return [
            ...Object.entries(mains).map(([code, name]) => ({code, name})),
            ...Object.entries(countries).map(([code, name]) => ({code, name})),
        ];
    }, [countries, mains]);

    const value = useMemo(() => {
        return options.filter((opt) => countrySearch.includes(opt.code));
    }, [options, countrySearch]);

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            value={value}
            isOptionEqualToValue={(option, val) => option.code === val.code}
            getOptionLabel={(option) => option.name}
            onChange={(_event, newValue) => {
                setCountrySearch(newValue.map((o) => o.code));
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({index})} />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={t("last_user_locations_country_filter_placeholder")}
                    sx={{
                        backgroundColor: "white",
                        borderRadius: 1
                    }}
                />
            )}
            sx={{color: "blue"}}
        />
    );
}
